import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBeer, FaBriefcase, FaDesktop, FaHandshake, FaGlobe, FaCrosshairs, FaLightbulb, FaEnvelope, FaPhone, FaMapMarkerAlt, FaBars, FaTimes, FaCheckCircle, FaTimesCircle, FaProjectDiagram } from 'react-icons/fa';
import './App.css';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.service || !formData.message) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    // Simulate form submission
    showToast('Thank you! Your message has been sent successfully. We\'ll get back to you soon.', 'success');

    // Reset form
    setFormData({
      name: '',
      email: '',
      company: '',
      service: '',
      message: ''
    });
  };

  return (
    <div className="App">
      {/* Navigation */}
      <motion.nav
        className="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="nav-container">
          <motion.div
            className="logo"
            whileHover={{ scale: 1.05 }}
          >
            <img src="/logo.png" alt="Talent Consulting PSA" className="logo-image" />
          </motion.div>

          <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            <a href="#home" onClick={() => scrollToSection('home')} className={activeSection === 'home' ? 'active' : ''}>Home</a>
            <a href="#about" onClick={() => scrollToSection('about')} className={activeSection === 'about' ? 'active' : ''}>About</a>
            <a href="#services" onClick={() => scrollToSection('services')} className={activeSection === 'services' ? 'active' : ''}>Services</a>
            <a href="/culture" className="nav-link">Culture</a>
            <a href="#contact" onClick={() => scrollToSection('contact')} className={activeSection === 'contact' ? 'active' : ''}>Contact</a>
          </div>

          <div className="hamburger" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="hero-text"
          >
            <h1>Talent Consulting PSA</h1>
            <p className="hero-subtitle">Connecting Global Talent with Innovation</p>
            <p className="hero-description">
              A forward-thinking company serving clients across the United States, Canada,
              United Kingdom, Australia, Singapore, the European Union, and Switzerland.
            </p>
            <motion.button
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('services')}
            >
              Discover Our Services
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="hero-visual"
          >
            <div className="floating-cards">
              <motion.div
                className="card"
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="card-content">
                  <FaBriefcase />
                  <span>IT Recruitment</span>
                </div>
              </motion.div>
              <motion.div
                className="card"
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1 }}
              >
                <div className="card-content">
                  <FaDesktop />
                  <span>IT Consultancy</span>
                </div>
              </motion.div>
              <motion.div
                className="card"
                animate={{ y: [0, -25, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 2 }}
              >
                <div className="card-content">
                  <FaBeer />
                  <span>Beer Restaurant</span>
                </div>
              </motion.div>
              <motion.div
                className="card"
                animate={{ y: [0, -18, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
              >
                <div className="card-content">
                  <FaProjectDiagram />
                  <span>Project Management</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>About Talent Consulting PSA</h2>
            <p>Founded and led by Andrii Lebedynets, we combine expertise in IT services with a passion for community hospitality.</p>
            <p className="legal-name">Legal entity: TALENT CONSULTING PROSTA SPÓŁKA AKCYJNA</p>
          </motion.div>

          <div className="about-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="about-text"
            >
              <h3>Our Story</h3>
              <p>
                Based in Piotrków Trybunalski, Poland, we've built a reputation for delivering
                exceptional value both locally and globally. Our unique combination of IT expertise
                and hospitality creates a welcoming environment for both our clients and community.
              </p>

              <div className="why-choose-us">
                <h4>Why Clients Choose Us</h4>
                <div className="features-grid">
                  <div className="feature">
                    <FaGlobe className="feature-icon" />
                    <h5>Global Reach</h5>
                    <p>Trusted by clients across North America, Europe, Asia-Pacific, and Switzerland</p>
                  </div>
                  <div className="feature">
                    <FaCrosshairs className="feature-icon" />
                    <h5>Tailored Solutions</h5>
                    <p>Customized strategies that align with each client's unique business goals</p>
                  </div>
                  <div className="feature">
                    <FaLightbulb className="feature-icon" />
                    <h5>Innovation & Experience</h5>
                    <p>Modern, reliable, and scalable services guided by experienced leadership</p>
                  </div>
                  <div className="feature">
                    <FaHandshake className="feature-icon" />
                    <h5>Trusted Partnerships</h5>
                    <p>Building long-term relationships based on transparency and results</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="about-visual"
            >
              <div className="stats-container">
                <div className="stat">
                  <h3>7+</h3>
                  <p>Countries Served</p>
                </div>
                <div className="stat">
                  <h3>15+</h3>
                  <p>Projects Completed</p>
                </div>
                <div className="stat">
                  <h3>2+</h3>
                  <p>Years Experience</p>
                </div>
                <div className="stat">
                  <h3>8+</h3>
                  <p>Global Clients</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>Our Expertise</h2>
            <p>Delivering value through diverse services that combine technology and hospitality</p>
          </motion.div>

          <div className="services-grid">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="service-card"
            >
              <div className="service-image">
                <img src="/project-management.png" alt="Project Management" className="service-img" />
              </div>
              <h3>IT Project Consultancy</h3>
              <p>Supporting organizations with strategy, execution, and oversight for smooth and successful IT projects. From planning to delivery, we ensure your success.</p>
              <ul>
                <li>Project Strategy</li>
                <li>Technical Architecture</li>
                <li>Quality Assurance</li>
                <li>Risk Management</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="service-card"
            >
              <div className="service-image">
                <img src="/recruiting.webp" alt="IT Recruitment" className="service-img" />
              </div>
              <h3>IT Recruitment</h3>
              <p>Connecting businesses with highly skilled tech professionals across international markets. We specialize in finding the right talent for your specific needs.</p>
              <ul>
                <li>Global Talent Pool</li>
                <li>Technical Screening</li>
                <li>Cultural Fit Assessment</li>
                <li>Ongoing Support</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="service-card"
            >
              <div className="service-image">
                <img src="/consulting.webp" alt="IT Consulting" className="service-img" />
              </div>
              <h3>IT Consulting</h3>
              <p>Providing strategic technology guidance and expertise to help organizations optimize their IT infrastructure, processes, and digital transformation initiatives.</p>
              <ul>
                <li>Technology Strategy</li>
                <li>Digital Transformation</li>
                <li>Process Optimization</li>
                <li>Technical Advisory</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="clients">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>Trusted by Global Leaders</h2>
            <p>We're proud to work with innovative companies across the world</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="clients-slider"
          >
            <div className="slider-track">
              <div className="client-slide">
                <img src="/walmart-logo.png" alt="Walmart" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/staffomatic-logo.png" alt="Staffomatic" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/gbg-logo.png" alt="GBG" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/ironbelly-studios-logo.png" alt="Ironbelly Studios" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/singular-logo.png" alt="Singular" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/bonify-logo.webp" alt="Bonify" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/dynatex-logo.jpg" alt="Dynatex" className="client-logo-img" />
              </div>
              {/* Duplicate for seamless loop */}
              <div className="client-slide">
                <img src="/walmart-logo.png" alt="Walmart" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/staffomatic-logo.png" alt="Staffomatic" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/gbg-logo.png" alt="GBG" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/ironbelly-studios-logo.png" alt="Ironbelly Studios" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/singular-logo.png" alt="Singular" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/bonify-logo.webp" alt="Bonify" className="client-logo-img" />
              </div>
              <div className="client-slide">
                <img src="/dynatex-logo.jpg" alt="Dynatex" className="client-logo-img" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="clients-stats"
          >
            <div className="stat-item">
              <h3>8+</h3>
              <p>Global Clients</p>
            </div>
            <div className="stat-item">
              <h3>6</h3>
              <p>Countries</p>
            </div>
            <div className="stat-item">
              <h3>100%</h3>
              <p>Client Satisfaction</p>
            </div>
            <div className="stat-item">
              <h3>24/7</h3>
              <p>Support</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>Get In Touch</h2>
            <p>Ready to work with us? Let's discuss how we can help your business grow.</p>
          </motion.div>

          <div className="contact-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="contact-info"
            >
              <div className="contact-item">
                <FaMapMarkerAlt className="contact-icon" />
                <div>
                  <h4>Address</h4>
                  <p>ul. Szewska 2, 97-300<br />Piotrków Trybunalski, Poland</p>
                </div>
              </div>

              <div className="contact-item">
                <FaPhone className="contact-icon" />
                <div>
                  <h4>Phone</h4>
                  <p>+48 459 569 723 +1 209 778 5909</p>
                </div>
              </div>

              <div className="contact-item">
                <FaEnvelope className="contact-icon" />
                <div>
                  <h4>Email</h4>
                  <p>andrii.lebedynets@talentconsulting.contact</p>
                </div>
              </div>

              <div className="business-hours">
                <h4>Business Hours</h4>
                <p>Monday - Friday: 9:00 AM - 6:00 PM (CET)</p>
                <p>Saturday: 10:00 AM - 4:00 PM (CET)</p>
                <p>Sunday: Closed</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="map-container"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2490.1234567890123!2d19.6975!3d51.4056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471a123456789abc%3A0x1234567890abcdef!2sul.%20Szewska%202%2C%2097-300%20Piotrk%C3%B3w%20Trybunalski%2C%20Poland!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '15px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Talent Consulting PSA Location"
              ></iframe>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="contact-form"
            >
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="company"
                    placeholder="Company"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Service</option>
                    <option value="recruitment">IT Recruitment</option>
                    <option value="consultancy">IT Consultancy</option>
                    <option value="restaurant">Beer Restaurant</option>
                    <option value="partnership">Business Partnership</option>
                  </select>
                </div>
                <div className="form-group">
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                <motion.button
                  type="submit"
                  className="submit-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Send Message
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="/logo.png" alt="Talent Consulting PSA" className="logo-image" />
            </div>
            <p>&copy; 2025 TALENT CONSULTING PROSTA SPÓŁKA AKCYJNA. All rights reserved.</p>
            <p>Connecting Global Talent with Innovation</p>
          </div>
        </div>
      </footer>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.3 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.3 }}
            transition={{ duration: 0.3 }}
            className={`toast ${toast.type}`}
          >
            <div className="toast-content">
              {toast.type === 'success' ? (
                <FaCheckCircle className="toast-icon" />
              ) : (
                <FaTimesCircle className="toast-icon" />
              )}
              <span className="toast-message">{toast.message}</span>
            </div>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
            >
              <FaTimes />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
